/**
 * Klartext für die Datenbankschlüssel der Aussparungen und Brandschotts —
 * Bauarten, Medien, Gewerke, Durchgangsarten, Zustände, Herkünfte,
 * Schott-Herkünfte, Schottarten, Paarungsarten, Ampelfarben.
 *
 * ════════════════════════════════════════════════════════════════════════
 * WARUM ES DIESE DATEI GIBT
 * ════════════════════════════════════════════════════════════════════════
 *
 * Am 23.09.2026 druckte das Brandschottkataster an allen 2.036 Zeilen den
 * Datenbankschlüssel statt des Klartextes (`tt_platte`, `kaltwasser_trink`).
 * Die Übersetzung gab es dreimal in C# und zweimal hier — `BAUART` in
 * `AussparungArbeit.tsx`, `BAUART_NAME` in `AussparungBaum.tsx`, dazu zwei
 * Teillisten (`BAUARTEN` in `Aussparungen.tsx` kannte VIER Bauarten von
 * sieben) und die Paarungsarten in `AussparungAbgleich.tsx`.
 *
 * Sechs Teillisten, die nichts voneinander wissen, sind schlechter als eine:
 * Sie sehen aus wie eine Übersetzung und sind eine Stichprobe.
 *
 * ════════════════════════════════════════════════════════════════════════
 * EIN WORT, DAS SCHÖNER KLINGT, DARF NICHTS ANDERES BEDEUTEN
 * ════════════════════════════════════════════════════════════════════════
 *
 * `geprueft_ohne` heißt „geprüft, ohne Schott" und NICHT „geprüft";
 * `bauteil_beruehrend` heißt „aus berührendem Bauteil" — eine Herkunft
 * zweiter Güte; `zuschlag` heißt „über den Zuschlag (Toleranz)" und nicht
 * „ermittelt"; `entfernt` heißt „Entfernt" und nicht „gelöscht".
 *
 * ════════════════════════════════════════════════════════════════════════
 * EIN UNBEKANNTER SCHLÜSSEL BLEIBT SICHTBAR
 * ════════════════════════════════════════════════════════════════════════
 *
 * Der Rückfall ist NICHT der leere Text und nicht „—", sondern der Schlüssel
 * selbst mit Unterstrichen als Leerzeichen und großem Anfangsbuchstaben.
 *
 * ════════════════════════════════════════════════════════════════════════
 * GESPIEGELT AUS DER DOMÄNE
 * ════════════════════════════════════════════════════════════════════════
 *
 * Zwei Laufzeiten, kein gemeinsamer Code — die Tabellen stehen deshalb ein
 * zweites Mal in `src/Tuv.Export.Domain/AussparungKlartext.cs`. Ändert sich
 * ein Wert hier, muss er dort mitgezogen werden, und umgekehrt.
 * `alleSchluessel` ist die Liste, gegen die `AussparungKlartextTests`
 * zählt.
 */

/** Aus `neue_bauart` wird „Neue bauart". */
export function rueckfall(schluessel: string): string {
  if (schluessel.length === 0) return schluessel;

  const text = schluessel.replace(/_/g, ' ');

  return text.charAt(0).toUpperCase() + text.slice(1);
}

function nachschlagen(
  schluessel: string | null | undefined,
  tabelle: Record<string, string>,
  leer = '—',
): string {
  if (schluessel == null || schluessel.trim() === '') return leer;

  return tabelle[schluessel] ?? rueckfall(schluessel);
}

// --- Die Bauarten ---------------------------------------------------------
//
// Die Bauart des DURCHBROCHENEN Bauteils. Sie bestimmt das Schott: Conlit
// Pyrostat hat je ein Prüfzeugnis für Massivbau und Leichtbau.
// `decke` steht in keiner Spalte der Datenbank — der Baum des Revit-Fensters
// führt sie als Gruppe.
const bauartTabelle: Record<string, string> = {
  stahlbeton: 'Stahlbeton',
  tt_platte: 'TT-Platte',
  trockenbau: 'Trockenbau',
  mauerwerk: 'Mauerwerk',
  unterzug: 'Unterzug',
  stuetze: 'Stütze',
  holz: 'Holz',
  decke: 'Decke',
};

export const bauartname = (s: string | null | undefined): string =>
  nachschlagen(s, bauartTabelle);

// --- Die Medien -----------------------------------------------------------
//
// 691 der 2.036 Katasterzeilen tragen in der Spalte `Medium` das
// GEWERKKÜRZEL. Deshalb sucht mediumname() in zwei Tabellen; die
// Schlüsselmengen überschneiden sich nicht.
const mediumTabelle: Record<string, string> = {
  schmutzwasser: 'Schmutzwasser',
  regenwasser: 'Regenwasser',
  kaltwasser_trink: 'Trinkwasser kalt',
  warmwasser: 'Trinkwasser warm',
  fettabwasser: 'Fettabwasser',
  kondensat: 'Kondensat',
  heizung: 'Heizung',
  kaelte: 'Kälte',
  sprinkler: 'Sprinkler',
  diesel: 'Diesel',
};

// --- Die Gewerke ----------------------------------------------------------
const gewerkTabelle: Record<string, string> = {
  san: 'Sanitär',
  hzg: 'Heizung',
  klt: 'Kälte',
  rlt: 'Lüftung',
  elt: 'Elektro',
  spr: 'Sprinkler',
  tga: 'TGA (Gewerk offen)',
};

export const gewerkname = (s: string | null | undefined): string =>
  nachschlagen(s, gewerkTabelle);

/** Das Medium — und ersatzweise das Gewerk, das in derselben Spalte steht. */
export function mediumname(s: string | null | undefined): string {
  if (s == null || s.trim() === '') return '—';

  return mediumTabelle[s] ?? gewerkTabelle[s] ?? rueckfall(s);
}

// --- Die Durchgangsarten --------------------------------------------------
const artTabelle: Record<string, string> = {
  rohr: 'Rohr',
  kanal: 'Kanal',
  trasse: 'Kabeltrasse',
  leerrohr: 'Leerrohr',
  sprinkler: 'Sprinkler',
  sonstiges: 'sonstiges',
};

export const artname = (s: string | null | undefined): string =>
  nachschlagen(s, artTabelle);

// --- Die Zustände ---------------------------------------------------------
//
// `entfernt` ist der einzige mit IstAktiv=0 — und heißt NICHT „gelöscht":
// die Zeile bleibt stehen, die Katasternummer bleibt vergeben.
const zustandTabelle: Record<string, string> = {
  manuell: 'von Hand gesetzt',
  aktuell: 'aktuell',
  erstellt: 'erstellt',
  entfernt: 'Entfernt',
};

export const zustandname = (s: string | null | undefined): string =>
  nachschlagen(s, zustandTabelle);

// --- Die Herkünfte --------------------------------------------------------
//
// Die Herkunft sagt, was eine Angabe wert ist. `zuschlag` ist eine
// Zuordnung über eine Toleranz, kein Schnitt; `kategorie` ist eingestuft,
// nicht belegt; `bauteil_beruehrend` ist zweiter Güte.
const herkunftTabelle: Record<string, string> = {
  schnitt: 'aus dem Schnitt',
  zuschlag: 'über den Zuschlag (Toleranz)',
  geometrisch: 'aus der Geometrie',

  kategorie: 'aus der Kategorie eingestuft',
  schichtaufbau: 'aus dem Schichtaufbau belegt',
  materialname: 'aus dem Materialnamen gelesen',

  bauteil: 'aus dem durchbrochenen Bauteil',
  bauteil_beruehrend: 'aus berührendem Bauteil',
  modell: 'aus dem Modell',

  kennzeichen: 'über das Kennzeichen',
  lage: 'über die Lage',

  auftrag: 'Festlegung des Bauherrn',
  ausgelegt: 'ausgelegt',
  offen: 'offen',
};

export const herkunftname = (s: string | null | undefined): string =>
  nachschlagen(s, herkunftTabelle);

// --- Die Schott-Herkünfte -------------------------------------------------
//
// `geprueft_ohne` heißt „geprüft, ohne Schott": Die Prüfung hat
// stattgefunden und endete mit „hier ist nichts einzubauen" (F0-Bauteil).
const schottherkunftTabelle: Record<string, string> = {
  gerechnet: 'nach Regel gerechnet',
  offen: 'offen — Zuordnung unvollständig',
  anforderung: 'Anforderung an die Fachplanung',
  geprueft_ohne: 'geprüft, ohne Schott',
};

export const schottherkunftname = (s: string | null | undefined): string =>
  nachschlagen(s, schottherkunftTabelle);

// --- Die Schottarten ------------------------------------------------------
const schottartTabelle: Record<string, string> = {
  rohrschott: 'Rohrabschottung',
  weichschott: 'Weichschott',
  blockschott: 'Blockschott',
  kabelschott: 'Kabelschott',
  kombischott: 'Kombischott',
  moertelschott: 'Mörtelschott',
  brandschutzklappe: 'Brandschutzklappe',
  schale: 'Schale',

  brandschutzklappe_erforderlich: 'Brandschutzklappe erforderlich',
  nicht_erforderlich: 'nicht erforderlich',
  offen: 'offen',
};

export const schottartname = (s: string | null | undefined): string =>
  nachschlagen(s, schottartTabelle);

// --- Der Prüfstand der Zulassungsdeckung (AS6, F88/F91) -------------------
//
// KEINE Tabelle, weil der Wert keine Zeichenkette ist: `ZulassungPasst` ist
// dreiwertig — true, false, null. Genau darin liegt die Aussage:
//
//   true    gedeckt      die Prüfung ist gelaufen und trägt
//   false   passt nicht  ein Kriterium ist NACHWEISLICH verletzt
//   null    ungeprüft    es fehlt die Angabe, gegen die zu prüfen wäre
//
// Die TT-Platte schlägt alles (F88): Für sie gibt es keine Schottregel und
// keine Bohrgrenze, und keiner der Bescheide nennt sie. „gedeckt" behauptete
// dort eine Prüfung, die es nicht gibt; „passt nicht" eine Ablehnung, die
// der Bescheid nicht ausspricht.
//
// Dieselbe Rangfolge steht in `SchottnachweisPdf.Pruefstand` — Blatt und
// Bildschirm dürfen einen Einbau nicht verschieden benennen.
export const BauartOhneRegel = 'tt_platte';

export function zulassungsstand(
  passt: boolean | null | undefined,
  bauart?: string | null,
): string {
  if (bauart === BauartOhneRegel) return 'Regel offen (F88)';

  if (passt === true) return 'gedeckt';
  if (passt === false) return 'passt nicht';

  return 'ungeprüft';
}

/** Die Ampelfarbe zum Prüfstand — „ungeprüft" ist keine gute Nachricht. */
export function zulassungsstandZustand(
  passt: boolean | null | undefined,
  bauart?: string | null,
): 'gut' | 'warnung' | 'fehler' {
  if (bauart === BauartOhneRegel) return 'warnung';

  if (passt === true) return 'gut';
  if (passt === false) return 'fehler';

  return 'warnung';
}

// --- Die Paarungsarten ----------------------------------------------------
//
// `verschoben` ist der teuerste Fall — im Modell fällt er nicht auf, aber
// die Leitung passt nicht mehr durch.
const paarungTabelle: Record<string, string> = {
  uebereinstimmend: 'übereinstimmend',
  verschoben: 'verschoben',
  groesse_abweichend: 'Größe weicht ab',
  nur_convoid: 'nur in conVoid',
  nur_architektur: 'nur im Architekturmodell',
};

export const paarungname = (s: string | null | undefined): string =>
  nachschlagen(s, paarungTabelle);

// --- Die Versatzstufen (F90, AS8) -----------------------------------------
//
// Die Stufe sagt, WIE WEIT zwei Modelle auseinanderliegen - nicht, welches
// recht hat. Die Grenzen stehen als Parameter in der Datenbank (0822); hier
// steht nur das Wort dazu. `ohne_abstand` ist keine Stufe, sondern ihre
// Abwesenheit: Auf einer Seite fehlt die Lage.
const stufenTabelle: Record<string, string> = {
  toleranz: 'in Toleranz',
  befund: 'über der Grenze',
  anderer_ort: 'anderer Ort',
  ohne_abstand: 'kein Abstand gemessen',
};

export const stufenname = (s: string | null | undefined): string =>
  nachschlagen(s, stufenTabelle);

// --- Die Entscheidung des Fachplaners (AS8, 0823) -------------------------
//
// NICHT dasselbe wie „Paarung bestätigt": Das sagt „das sind dieselben zwei
// Löcher", dies sagt „dieser Stand gilt". Und sie ändert kein Modell (F61) -
// „die Architektur führt" ist eine Anweisung an den Planer.
const entscheidungTabelle: Record<string, string> = {
  offen: 'offen — noch nicht entschieden',
  bestaetigt: 'bestätigt — beide Stände stimmen überein',
  architektur_fuehrt: 'die Architektur führt — conVoid zieht nach',
  convoid_fuehrt: 'conVoid führt — die Architektur zieht nach',
  zurueckgestellt: 'zurückgestellt — nicht entscheidbar',
};

export const entscheidungname = (s: string | null | undefined): string =>
  s == null || s === '' ? entscheidungTabelle.offen
    : nachschlagen(s, entscheidungTabelle);

// --- Die Ampelfarben ------------------------------------------------------
//
// `grau` ist NICHT „unbekannt": Es hat noch niemand hingesehen.
const ampelTabelle: Record<string, string> = {
  gruen: 'Freigegeben',
  gelb: 'Offene Punkte',
  rot: 'Abgelehnt',
  blau: 'In Prüfung',
  grau: 'Nicht eingereicht',
};

export const ampelname = (s: string | null | undefined): string =>
  nachschlagen(s, ampelTabelle);

// --- Der Raumbefund -------------------------------------------------------
const raumbefundTabelle: Record<string, string> = {
  kein_raum: 'kein Raum getroffen',
  schacht: 'Schacht',
};

export const raumbefundname = (s: string | null | undefined): string =>
  nachschlagen(s, raumbefundTabelle);

// --- Die Raumangabe -------------------------------------------------------

/**
 * Der Raumname ohne die angehängte Raumnummer — oder unverändert, wenn er
 * nicht auf sie endet.
 */
export function ohneAngehaengteNummer(name: string, nummer: string): string {
  const nm = name.trim();
  const nr = nummer.trim();

  if (nr.length === 0 || nm.length <= nr.length) return nm;
  if (!nm.endsWith(nr)) return nm;

  // Nur eine ANGEHÄNGTE Nummer, kein Wortende: „E0-1234" endet auf „234",
  // ist aber nicht „E0-1" plus Nummer.
  const davor = nm.charAt(nm.length - nr.length - 1);

  if (davor !== ' ' && davor !== '\t' && davor !== '-' && davor !== '·')
    return nm;

  return nm.slice(0, nm.length - nr.length - 1).replace(/[ \t\-·]+$/, '');
}

/**
 * „E1-176 Küche Musicland Süd" — die Raumnummer EINMAL.
 *
 * `aussparung_raeume.Raumname` endet an 9.629 von 9.630 Zeilen selbst auf
 * die Raumnummer. Wer Nummer und Namen aneinanderhängt, druckt
 * „E1-176 Küche Musicland Süd E1-176". DIE DATEN BLEIBEN, WIE SIE SIND —
 * bereinigt wird beim Anzeigen, an dieser einen Stelle, die Web und PDF
 * gemeinsam benutzen (`AussparungKlartext.Raumangabe`).
 */
export function raumangabe(
  nummer: string | null | undefined,
  name: string | null | undefined,
): string {
  const nr = (nummer ?? '').trim();
  const nm = (name ?? '').trim();

  if (nr === '' && nm === '') return '—';
  if (nr === '') return nm;
  if (nm === '') return nr;

  // Der Name IST die Nummer: einmal reicht.
  if (nm === nr) return nr;

  const kurz = ohneAngehaengteNummer(nm, nr);

  return kurz.length === 0 ? nr : `${nr} ${kurz}`;
}

/**
 * Alle Schlüssel je Gruppe — die Liste, gegen die gezählt wird.
 *
 * Sie muss Zeichen für Zeichen dieselbe sein wie
 * `AussparungKlartext.AlleSchluessel` in der Domäne.
 */
export const alleSchluessel: Record<string, string[]> = {
  bauart: Object.keys(bauartTabelle),
  medium: Object.keys(mediumTabelle),
  gewerk: Object.keys(gewerkTabelle),
  art: Object.keys(artTabelle),
  zustand: Object.keys(zustandTabelle),
  herkunft: Object.keys(herkunftTabelle),
  schottherkunft: Object.keys(schottherkunftTabelle),
  schottart: Object.keys(schottartTabelle),
  paarung: Object.keys(paarungTabelle),
  ampel: Object.keys(ampelTabelle),
  raumbefund: Object.keys(raumbefundTabelle),
};
